import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  type FriendDto,
  type MyFriendCodeDto,
  type AddFriendResponseDto,
  type AddFriendsFromGroupResponseDto,
} from "@tabipla/shared";
import { friendKeys } from "./query-keys";
import type {
  UseFriendsOptions,
  UseMyFriendCodeOptions,
  UseAddFriendOptions,
  UseRemoveFriendOptions,
  UseAddFriendsFromGroupOptions,
} from "./types";

/**
 * 友達一覧を取得するフック
 *
 * @example
 * const { data: friends, isLoading } = useFriends({ apiClient });
 */
export function useFriends(options: UseFriendsOptions) {
  const { apiClient, enabled = true } = options;

  return useQuery({
    queryKey: friendKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<FriendDto[]>("/api/friends");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "友達一覧の取得に失敗しました");
      }
      return response.data!;
    },
    enabled,
  });
}

/**
 * 自分のフレンドコードを取得するフック
 *
 * @example
 * const { data: myCode } = useMyFriendCode({ apiClient });
 * console.log(myCode?.friendCode); // "ABC12345"
 */
export function useMyFriendCode(options: UseMyFriendCodeOptions) {
  const { apiClient, enabled = true } = options;

  return useQuery({
    queryKey: friendKeys.myCode(),
    queryFn: async () => {
      const response = await apiClient.get<MyFriendCodeDto>("/api/friends/my-code");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "フレンドコードの取得に失敗しました");
      }
      return response.data!;
    },
    enabled,
  });
}

/**
 * 友達を追加するフック（フレンドコードで）
 *
 * @example
 * const addFriend = useAddFriend({ apiClient });
 * await addFriend.mutateAsync("ABC12345");
 */
export function useAddFriend(options: UseAddFriendOptions) {
  const { apiClient, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (friendCode: string) => {
      const response = await apiClient.post<AddFriendResponseDto>("/api/friends", {
        friendCode,
      });
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "友達の追加に失敗しました");
      }
      return response.data!;
    },
    onSuccess: (data) => {
      // 友達リストに新しい友達を追加
      queryClient.setQueryData<FriendDto[]>(friendKeys.list(), (old) => {
        if (!old) return [data.friend];
        return [...old, data.friend];
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

/**
 * 友達を削除するフック
 *
 * @example
 * const removeFriend = useRemoveFriend({ apiClient });
 * await removeFriend.mutateAsync("friend-user-id");
 */
export function useRemoveFriend(options: UseRemoveFriendOptions) {
  const { apiClient, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (friendId: string) => {
      const response = await apiClient.delete<null>(`/api/friends/${friendId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "友達の削除に失敗しました");
      }
      return friendId;
    },
    onSuccess: (friendId) => {
      // 友達リストから削除
      queryClient.setQueryData<FriendDto[]>(friendKeys.list(), (old) => {
        if (!old) return old;
        return old.filter((f) => f.friendId !== friendId);
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}

/**
 * グループメンバーから友達を追加するフック
 *
 * @example
 * const addFriendsFromGroup = useAddFriendsFromGroup({
 *   apiClient,
 *   groupId: "group-id"
 * });
 * await addFriendsFromGroup.mutateAsync(["user-id-1", "user-id-2"]);
 */
export function useAddFriendsFromGroup(options: UseAddFriendsFromGroupOptions) {
  const { apiClient, groupId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberIds: string[]) => {
      const response = await apiClient.post<AddFriendsFromGroupResponseDto>(
        `/api/friends/from-group/${groupId}`,
        { memberIds }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "友達の追加に失敗しました");
      }
      return response.data!;
    },
    onSuccess: () => {
      // 友達リストを再取得
      queryClient.invalidateQueries({ queryKey: friendKeys.list() });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}
